import type { IApiHandler } from './IApiHandler';
import {
    AccountsService,
    AnalyticsService,
    AttributeTypesService,
    AuthService,
    BranchesService,
    BrandsService,
    CatalogImportsService,
    CategoriesService,
    DividendsService,
    ImagesService,
    FixedAssetsService,
    JournalService,
    OperationsService,
    OrganizationsService,
    PeriodsService,
    SalariesService,
    LedgerSettingsService,
    StatementsService,
    ProductsService,
    PurchasesService,
    SalesService,
    ShiftsService,
    SuppliersService,
    TrialBalanceService,
    UsersService,
    VatService,
    type IAccountsService,
    type IAttributeTypesService,
    type IAuthService,
    type IBranchesService,
    type IBrandsService,
    type ICatalogImportsService,
    type ICategoriesService,
    type IDividendsService,
    type IFixedAssetsService,
    type IImagesService,
    type IJournalService,
    type IOperationsService,
    type IOrganizationsService,
    type IPeriodsService,
    type IProductsService,
    type IPurchasesService,
    type IAnalyticsService,
    type ISalariesService,
    type ISalesService,
    type ILedgerSettingsService,
    type IShiftsService,
    type IStatementsService,
    type ISuppliersService,
    type ITrialBalanceService,
    type IUsersService,
    type IVatService,
} from './resources';

/**
 * Every accounting domain is live: production passes nothing but `baseUrl` and
 * gets a real HTTP service for all of them.
 *
 * The overrides below exist for **tests**, and for a local environment with no
 * reachable ledger. Left live, a test that renders one of these screens issues a
 * real request to a host that is not there and sits in a loading state until it
 * times out — a slow test that eventually becomes a flaky one.
 *
 * What to pass depends on what the test is asserting:
 *
 * - **Screen tests** want data to render against. `testApiProvider` passes the
 *   seeded doubles in `tests/mocks/` for the three domains that still have one
 *   (accounts, journal, tax settings).
 * - **Contract tests** want the *live* mapping exercised, so they stub the
 *   service with wire-shaped fixtures run through that domain's mapper — see
 *   the `live*Stub.ts` files under `tests/api/`.
 *
 * The old per-domain mocks are gone; each was deleted as its API shipped,
 * together with the engine it owned — depreciation schedules, payroll and
 * distribution arithmetic, the ledger overlay — all of which the server computes
 * now.
 */
type ApiHandlerOptions = {
    baseUrl: string;
    accounts?: IAccountsService;
    /**
     * The shift register's branch filter reads the whole branch set on mount.
     * Without this override a test that renders it issues a real request to a
     * host that is not there.
     */
    branches?: IBranchesService;
    /**
     * The import flow previews on every re-check and imports on submit. A
     * test that drives it without this override posts to a host that is not
     * there.
     */
    catalogImports?: ICatalogImportsService;
    dividends?: IDividendsService;
    fixedAssets?: IFixedAssetsService;
    journal?: IJournalService;
    /**
     * The Templates tab reads the catalog on mount. Without this override a
     * test that renders it issues a real request to a host that is not there
     * and sits in a loading state until it times out.
     */
    operations?: IOperationsService;
    /**
     * The organisation is what routes a signed-in user to the onboarding
     * wizard or the shell. A test that renders a route behind that read
     * without this override issues a real request to a host that is not there
     * and waits on it.
     */
    organizations?: IOrganizationsService;
    /**
     * `getPeriodStatus` is an HTTP read and the entry form calls it on **every
     * date change** to drive the Post gate, so a form test without this override
     * gates on a request that never resolves.
     */
    periods?: IPeriodsService;
    salaries?: ISalariesService;
    /**
     * Nothing renders the settings screen incidentally, but several registers
     * read the rate set in force to price a distribution or a payroll run — the
     * shareholder form does it on mount. Without this, every test that touches
     * one logs a connection refusal (the screen still renders; the query fails
     * into the envelope).
     */
    settings?: ILedgerSettingsService;
    /**
     * The shift register and its rollup drawer. Same reason as `operations`: the
     * page reads on mount, and left live the request goes nowhere.
     */
    shifts?: IShiftsService;
    analytics?: IAnalyticsService;
    statements?: IStatementsService;
    trialBalance?: ITrialBalanceService;
    vat?: IVatService;
};

export class ApiHandler implements IApiHandler {
    public accounts: IAccountsService;
    public analytics: IAnalyticsService;
    public attributeTypes: IAttributeTypesService;
    public auth: IAuthService;
    public branches: IBranchesService;
    public brands: IBrandsService;
    public catalogImports: ICatalogImportsService;
    public categories: ICategoriesService;
    public dividends: IDividendsService;
    public fixedAssets: IFixedAssetsService;
    public images: IImagesService;
    public journal: IJournalService;
    public operations: IOperationsService;
    public organizations: IOrganizationsService;
    public periods: IPeriodsService;
    public products: IProductsService;
    public purchases: IPurchasesService;
    public salaries: ISalariesService;
    public sales: ISalesService;
    public settings: ILedgerSettingsService;
    public shifts: IShiftsService;
    public statements: IStatementsService;
    public suppliers: ISuppliersService;
    public trialBalance: ITrialBalanceService;
    public users: IUsersService;
    public vat: IVatService;

    constructor({
        baseUrl,
        accounts,
        analytics,
        branches,
        catalogImports,
        shifts,
        journal,
        operations,
        organizations,
        periods,
        statements,
        trialBalance,
        vat,
        fixedAssets,
        salaries,
        dividends,
        settings,
    }: ApiHandlerOptions) {
        // LIVE (BOS-91): the ledger accounts endpoints shipped, so the Chart of
        // Accounts reads and writes the real chart — and as of BOS-78 its
        // balances are the ledger's own, unmodified.
        this.accounts = accounts ?? new AccountsService({ baseUrl });
        this.attributeTypes = new AttributeTypesService({ baseUrl });
        this.auth = new AuthService({ baseUrl });
        this.branches = branches ?? new BranchesService({ baseUrl });
        this.brands = new BrandsService({ baseUrl });
        // LIVE (API-185): the per-branch catalog import at
        // `/api/v1/catalog-imports` — the preview and the import. The one
        // thing to know before touching anything nearby: an import refused
        // with `IMPORT_LINES_INVALID` carries the whole per-row result in its
        // body, and the service hands it back mapped so the table can be
        // refreshed from it. See `resources/catalogImports/CONTRACT.md`.
        this.catalogImports =
            catalogImports ?? new CatalogImportsService({ baseUrl });
        this.categories = new CategoriesService({ baseUrl });
        this.images = new ImagesService({ baseUrl });
        // LIVE (BOS-78): the posting engine shipped, so the journal list, the
        // entry form, the detail drawer and the chart drill-in all read and
        // write the real ledger at `/api/v1/ledger/journal-entries`.
        //
        // See `resources/journal/CONTRACT.md` for the verified contract. The one
        // thing to know before touching anything nearby: the server reports
        // `validationState.postable: true` for an entry dated in a **closed
        // period** and refuses only at post time, so the client's own
        // PERIOD_CLOSED rule is what actually gates the Post button. That rule
        // is only as good as the period data behind it — hence the wrapper
        // below.
        this.journal = journal ?? new JournalService({ baseUrl });
        // LIVE (task 01): the operation catalog at
        // `/api/v1/ledger/operations` — the code-defined posting flows merged
        // with the tenant's own editable templates.
        //
        // Two things to know before touching anything nearby:
        //
        // 1. **One row shape on the wire, two in the client.** The response
        //    flattens system rows and templates into one object with the
        //    inapplicable half nulled; `operationsMapping` narrows it to a union
        //    on `kind`. Deciding "is this editable?" from a nullable field is
        //    how a read-only row eventually gets an Edit button.
        // 2. **No `locale`.** Every row carries both names, because the tab
        //    shows both at once. A language switch re-renders; it does not
        //    refetch.
        this.operations = operations ?? new OperationsService({ baseUrl });
        // LIVE (API-185): the caller's organisation at
        // `/api/v1/organizations/me` — whether onboarding is finished and
        // whether accounting still has to be started — plus the two OrgAdmin
        // onboarding writes. See `resources/organizations/CONTRACT.md`.
        this.organizations =
            organizations ?? new OrganizationsService({ baseUrl });
        // LIVE: fiscal years, periods, the year-end close and the reopen all
        // read and write `/api/v1/ledger/fiscal-years/*`. The mock service, its
        // fixtures and the `withLivePeriodStatus` half-measure are all deleted.
        //
        // Four things to know before touching anything nearby, each measured
        // against a live response and each a silent failure if ignored:
        //
        // 1. **Everything is keyed by the integer year**, and a period by year +
        //    period number. The uuids in the payloads address nothing. Hence
        //    `FiscalYear.id` is `'2026'` and `AccountingPeriod.id` is `'2026:6'`.
        // 2. **Periods close strictly in order** but reopen in any order. The
        //    screen pre-empts the first with a stated reason; the second is why
        //    a reopen can leave an open month behind a closed one.
        // 3. **Sending `{ date }` on the close is honoured**, and would move the
        //    posted entry off the date the preview showed. The service sends no
        //    body at all — which is also what makes a `source` unsendable.
        // 4. **There is no rollover dry run.** `/rollover/preview` 404s, so the
        //    rollover action stays behind `LedgerHandoffNotice` — the only one
        //    of this screen's actions that does.
        //
        // See `resources/periods/periodsMapping.ts` for the full field-by-field
        // divergence, including the P&L double-count a reopen currently causes
        // server-side.
        this.periods = periods ?? new PeriodsService({ baseUrl });
        // LIVE: the Balance Sheet and Profit & Loss endpoints shipped, so both
        // statement screens read the real ledger at
        // `/api/v1/ledger/statements/*` — layout, figures, labels, comparatives
        // and deltas all server-computed.
        //
        // Three things to know before touching anything nearby, each measured
        // against a live response and each a silent failure if ignored:
        //
        // 1. **`valueTetri` is natural-terms; `sign` is presentation only.** A
        //    contra line arrives positive with `sign: -1`. `deltaTetri` is the
        //    server's own, also natural-terms — re-signing or recomputing it is
        //    what made a growing accumulated depreciation read as a decrease.
        // 2. **Warnings are not on the statement.** `StatementResponse` has no
        //    warnings field, so `/plausibility` is a second call. A consumer that
        //    forgets it renders a silently unbalanced balance sheet as fine.
        // 3. **Nil lines are suppressed by default.** `StatementsService` always
        //    sends `includeZeroLines=true`; without it a historical as-of with no
        //    activity collapses from 22 rows to 2.
        //
        // See `resources/statements/statementsMapping.ts` for the full field-by-
        // field divergence from what this client originally assumed.
        this.statements = statements ?? new StatementsService({ baseUrl });
        // LIVE: `/api/v1/ledger/analytics/financial-position` — the Analytics
        // area's single read. It composes the statements rather than duplicating
        // them, and it owns the bands that turn a figure into
        // Strained/Stable/Strong, because those are business policy: computing
        // them here as well would let the zone rail and the metric cards
        // describe the same business differently.
        //
        // Missing inputs are not errors. A metric whose source does not exist
        // yet arrives `PENDING` with a machine code naming what it waits for,
        // which the cards turn into the sentence on their empty state.
        this.analytics = analytics ?? new AnalyticsService({ baseUrl });
        // LIVE: the trial balance, at `/api/v1/ledger/trial-balance`. It is a
        // sibling of the statements rather than one of them — a working document
        // read while reconciling, not a published report — which is why the API
        // puts it at the ledger root and why it has its own service here.
        //
        // Three things to know before touching anything nearby, each measured
        // against a live response:
        //
        // 1. **The server assigns the columns.** Every row carries its own
        //    `debit` and `credit`, so nothing here places a figure by the sign of
        //    a balance — which is why accumulated depreciation appears on the
        //    credit side while still typed `ASSET`, with no client rule involved.
        // 2. **`balanced` is the server's verdict**, as are both totals. The
        //    ledger validates every entry as balanced before it can post, so a
        //    trial balance that does not balance is an engine defect; a client
        //    that recomputed the verdict could only disagree with the one that
        //    matters. `trialBalanceMapping` therefore fails the whole read rather
        //    than dropping an unreadable row and leaving the rows disagreeing
        //    with the totals printed beneath them.
        // 3. **Nil rows are dropped server-side and cannot be asked for.** There
        //    is no `includeZeroLines` counterpart here, so "every account with a
        //    balance" is the only list this endpoint can produce — the screen
        //    says so rather than implying the chart is as short as the report.
        this.trialBalance =
            trialBalance ?? new TrialBalanceService({ baseUrl });
        // LIVE (BOS-92): the VAT report and its settlement builder shipped, so
        // the screen reads `/api/v1/ledger/vat/*` — classification, both side
        // totals, the net and the drill composition all server-computed. The
        // mock is gone entirely: nothing else read it, so unlike the statements
        // fixture there was no non-user-facing consumer to contain.
        //
        // Four things to know before touching anything nearby, each measured
        // against a live response and each a silent failure if ignored:
        //
        // 1. **The report is one flat `accounts[]` tagged `role`**, not two
        //    sides. `vatMapping` partitions it on that tag and nothing else —
        //    input VAT sits under current assets and output VAT under current
        //    liabilities, so nothing about the tree or the codes can classify
        //    them, and the codes are still provisional besides.
        // 2. **A line's `vatAmountTetri` can be negative** — it is a directional
        //    movement, not a magnitude. Re-tagging a credit-balance account as
        //    `INPUT` returns a negative contribution.
        // 3. **`POST /ledger/vat/settlement` does not persist anything.** It
        //    builds a DRAFT with an id that 404s until the entry form saves it,
        //    and there is **no server-side duplicate guard** — two calls for the
        //    same period return two different ids. The panel detects an already
        //    posted settlement and says so; it promises nothing more.
        // 4. **The draft's `validationState` ignores the period gate.** It comes
        //    back `postable: true` for a date inside a CLOSED period; the entry
        //    form's own gate is what refuses the post.
        //
        // See `resources/vat/vatMapping.ts` for the full field-by-field
        // divergence from what this client originally assumed.
        this.vat = vat ?? new VatService({ baseUrl });
        // LIVE (BOS-93): the fixed-asset register, under
        // `/api/v1/ledger/fixed-assets`. It was the last accounting domain with
        // no backend at all — the mock *was* the contract — and the API has now
        // been built to it, with three concepts dropped along the way.
        //
        // The mock took the journal as a collaborator so a commit could move the
        // ledger by what the posted entry *actually* did rather than by what the
        // register predicted. **The server owns that read now**: it links a
        // posted entry to its asset itself, so there is no commit step and no
        // collaborator here. See `resources/fixedAssets/fixedAssetsMapping.ts`
        // for the full field-by-field divergence.
        this.fixedAssets = fixedAssets ?? new FixedAssetsService({ baseUrl });
        // LIVE (BOS-90): tax settings are the first accounting domain with a
        // real backend — versioned, effective-dated rate sets under
        // `/api/v1/ledger/tax-settings`. The settings screen reads and writes
        // these directly.
        this.settings = settings ?? new LedgerSettingsService({ baseUrl });
        // LIVE (BOS-109): the employee register and the payroll run, under
        // `/api/v1/ledger/salaries`. Like fixed assets it was mock-first — the
        // mock *was* the contract — and the API has now been built to it, with
        // four concepts changed along the way.
        //
        // Four things to know before touching anything nearby, each measured
        // against a live response and each a silent failure if ignored:
        //
        // 1. **Every route is `OrgAdmin`, reads included** — not `BranchAdmin` as
        //    the fixed-asset register is. These reads are org-wide and carry
        //    every colleague's salary, personal number and net pay. The screen
        //    gates itself on that (`salariesAccess`) rather than rendering and
        //    collecting 403s.
        // 2. **A run has no id.** A run *is* the journal entry stamped PAYROLL
        //    for its period, so `journalEntryId` is the id and it is null on a
        //    preview.
        // 3. **The commit builds the entry.** The old flow — client posts the
        //    entry, commit verifies it account by account, `409 ENTRY_MISMATCH`
        //    on a mismatch — is gone. `POST commit` creates the balanced DRAFT
        //    itself and returns it, which is what lets it go straight to
        //    `JournalEntryModal`. It posts nothing; the accrued figures move only
        //    when someone posts that draft.
        // 4. **The summary reports `accrued*`, not `outstanding*`**, and its
        //    three account ids are nullable. The register only sees what payroll
        //    accrued; payments and remittances are ledger events it never sees.
        //
        // Rates now come from the effective-dated `TaxRateSet`, resolved
        // server-side on the period's end date — so it takes neither the journal
        // nor settings as a collaborator any more. See
        // `resources/salaries/salariesMapping.ts` for the full field-by-field
        // divergence.
        this.salaries = salaries ?? new SalariesService({ baseUrl });
        // LIVE (BOS-112): the shareholder register and the distribution
        // declarations, under `/api/v1/ledger/dividends`. It was the **last**
        // accounting domain on a mock — the mock *was* the contract — and the API
        // has now been built to it. With it go the last two fixture instances:
        // the mock journal and the private mock statement feed existed only to
        // serve this register.
        //
        // Four things to know before touching anything nearby, each measured
        // against a live response and each a silent failure if ignored:
        //
        // 1. **Every route is `OrgAdmin`, reads included.** Distributable profit
        //    and who owns the company are not branch-level facts. The screen
        //    gates itself on that (`dividendsAccess`) rather than rendering and
        //    collecting 403s.
        // 2. **A declaration has no id and no status.** It *is* the journal entry
        //    stamped DIVIDEND, so `journalEntryId` is the identity — null on a
        //    preview — and `entryStatus` is the **entry's** status.
        // 3. **The commit builds the entry.** The old flow — client posts the
        //    entry, commit verifies it, `409 ENTRY_MISMATCH` on a mismatch — is
        //    gone. `POST commit` creates the balanced DRAFT itself and returns it,
        //    which is what lets it go straight to `JournalEntryModal`. It posts
        //    nothing; the accrued figures move only when someone posts that draft.
        // 4. **The summary reports only what has POSTED**, its three tie-out
        //    account ids are nullable, and `declaredUnpaidDividends` is the
        //    **net** the entry credited — not the gross it was declared at.
        //
        // Rates come from the effective-dated `TaxRateSet`, resolved server-side
        // on the declaration's **own date** — so it takes neither the journal,
        // settings nor the statements as a collaborator any more. See
        // `resources/dividends/dividendsMapping.ts` for the full field-by-field
        // divergence.
        this.dividends = dividends ?? new DividendsService({ baseUrl });
        this.products = new ProductsService({ baseUrl });
        this.purchases = new PurchasesService({ baseUrl });
        this.sales = new SalesService({ baseUrl });
        // LIVE (BOS-145): the back-office shift register and the per-shift
        // ledger rollup at `/api/v1/shifts`. Not the till's shift endpoints —
        // those sit behind a PIN-only policy this app can never satisfy.
        this.shifts = shifts ?? new ShiftsService({ baseUrl });
        this.suppliers = new SuppliersService({ baseUrl });
        this.users = new UsersService({ baseUrl });
    }
}
